import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, Phone, MessageCircle, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BackButton from "@/components/hola/BackButton";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error("Escribe los 10 dígitos de tu celular");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password-reset-request", { phone });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto px-6 py-10">
        <div className="mb-4 -ml-2">
          <BackButton to="/login" label="Regresar al login" testid="forgot-back-btn" />
        </div>
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#0055FF] text-white grid place-items-center">
            <Radar className="w-5 h-5" />
          </div>
          <span className="font-heading font-extrabold text-lg">HOLA RADAR</span>
        </Link>

        {!sent ? (
          <>
            <h1 className="font-heading text-3xl font-extrabold">Recuperar contraseña</h1>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              Escribe el celular con el que registraste tu comercio. Un administrador de HOLA RADAR revisará tu solicitud y te contactará por WhatsApp o llamada con una contraseña temporal.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="phone">Celular <span className="text-neutral-400 font-normal">(10 dígitos)</span></Label>
                <div className="mt-1.5 relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="phone"
                    data-testid="forgot-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="5512345678"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="pl-9 h-11 rounded-xl"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full h-12 rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold"
                data-testid="forgot-submit-btn"
              >
                <MessageCircle className="w-4 h-4" /> {loading ? "Enviando…" : "Solicitar contraseña temporal"}
              </Button>
              <p className="text-xs text-neutral-500 leading-relaxed">
                Un administrador revisa cada solicitud manualmente para proteger tu cuenta. Suele tardar menos de 24 horas hábiles.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="forgot-success">
            <div className="w-16 h-16 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold">Solicitud enviada</h1>
            <p className="text-sm text-neutral-600 mt-3 max-w-md mx-auto leading-relaxed">
              Si el celular <strong>{phone}</strong> corresponde a un comercio registrado, un administrador te contactará muy pronto con una contraseña temporal por WhatsApp o llamada.
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button onClick={() => navigate("/login")} className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold" data-testid="forgot-back-to-login">
                <ArrowLeft className="w-4 h-4" /> Volver al login
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
